// Eén bord van de meterkast (Spoor B, sessie B2): de metertabel.
//
// Eén rij per meter, één kolom per sector. In een cel staat wat de server
// stuurde: de waarde, de vorige waarde, het verschil en het stoplicht. Klikken
// op een meter opent de rijen erachter — het `detail` van de meting (B1 §11),
// niet een tweede aanroep: het antwoord draagt ze al.
//
// Waarom een tabel en geen kaarten: de vraag van dit scherm is "wat veranderde
// er", en die lees je door een kolom. Met meer sectoren wordt de tabel breder;
// hij schuift dan binnen zijn eigen kader, zodat de pagina zelf nooit
// horizontaal gaat schuiven (dezelfde regel als de andere beheertabellen).

import { Fragment, useState } from "react";

import { Card } from "../../components/ui";
import type {
  MeterkastBord as BordType,
  MeterkastMeter,
  MeterkastSectorwaarde,
  MeterkastSnede,
  MeterkastVergelijk,
} from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { cijfer } from "./BronnenStukken";
import { Detail } from "./MeterkastDetail";
import { breukTekst, verschilTekst, verschilUitleg, waardeTekst } from "./MeterkastStukken";
import { Stoplicht } from "./Stoplicht";
import { wieLabel, wieUitleg } from "./woorden";

const kop = {
  fontSize: size.columnLabel,
  fontWeight: weight.medium,
  color: color.faint,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  textAlign: "left",
  padding: "9px 14px",
  background: color.surfaceAlt,
  whiteSpace: "nowrap",
} as const;

const cel = {
  padding: "12px 14px",
  borderTop: `1px solid ${color.lineFaint}`,
  verticalAlign: "top",
} as const;

export function Bord({
  bord,
  sectoren,
  vergelijk,
  huidig,
  basis,
  onGewijzigd,
}: {
  bord: BordType;
  sectoren: readonly string[];
  vergelijk: MeterkastVergelijk;
  huidig: MeterkastSnede;
  basis: MeterkastSnede;
  onGewijzigd: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "15px 18px 13px" }}>
        <h2
          style={{
            margin: 0,
            fontSize: size.lead,
            fontWeight: weight.bold,
            color: color.ink,
            lineHeight: 1.35,
          }}
        >
          {`${bord.bord}. ${bord.naam}`}
        </h2>
      </div>

      {/* position: relative (reparatie 06-10): de voorleestekst in de kop staat
          absoluut; zonder eigen omvattend blok ontsnapte hij aan deze
          schuifcontainer en duwde hij de pagina op 390 px 80 px zijwaarts. */}
      <div style={{ overflowX: "auto", position: "relative" }}>
        <table
          data-bord={bord.bord}
          style={{ width: "100%", borderCollapse: "collapse", fontSize: size.meta }}
        >
          <thead>
            <tr>
              <th scope="col" style={{ ...kop, minWidth: 230 }}>
                Meter
              </th>
              {sectoren.map((s) => (
                <th key={s} scope="col" style={{ ...kop, minWidth: 170 }}>
                  {s}
                </th>
              ))}
              <th scope="col" style={{ ...kop, width: 1 }}>
                <span style={voorlezen}>De rijen erachter</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {bord.meters.map((m) => (
              <Fragment key={m.meter}>
                <MeterRij
                  meter={m}
                  sectoren={sectoren}
                  open={open === m.meter}
                  onOpen={() => setOpen(open === m.meter ? null : m.meter)}
                />
                {open === m.meter && (
                  <tr data-detail={m.meter}>
                    <td
                      colSpan={sectoren.length + 2}
                      style={{
                        padding: "4px 14px 18px",
                        background: color.surfaceQuote,
                        borderTop: `1px solid ${color.lineFaint}`,
                      }}
                    >
                      <Detail
                        meter={m}
                        vergelijk={vergelijk}
                        huidig={huidig}
                        basis={basis}
                        onGewijzigd={onGewijzigd}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function MeterRij({
  meter,
  sectoren,
  open,
  onOpen,
}: {
  meter: MeterkastMeter;
  sectoren: readonly string[];
  open: boolean;
  onOpen: () => void;
}) {
  const perSector = new Map(meter.waarden.map((w) => [w.sector, w]));
  return (
    <tr data-meter={meter.meter}>
      <th scope="row" style={{ ...cel, textAlign: "left", fontWeight: weight.regular }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ ...cijfer, color: color.faint, fontSize: size.label }}>{meter.meter}</span>
          <span style={{ color: color.ink, fontWeight: weight.medium, fontSize: size.body }}>
            {meter.naam}
          </span>
          <span
            title={wieUitleg(meter.wie) ?? undefined}
            style={{
              background: color.lineFaint,
              color: color.soft,
              borderRadius: radius.pill,
              padding: "2px 8px",
              fontSize: size.label,
              whiteSpace: "nowrap",
            }}
          >
            {wieLabel(meter.wie)}
          </span>
        </div>
        <p style={{ margin: "5px 0 0", color: color.muted, lineHeight: 1.5, maxWidth: 520 }}>
          {meter.uitleg}
        </p>
      </th>

      {sectoren.map((s) => (
        <td key={s} style={cel}>
          <Waarde waarde={perSector.get(s) ?? null} eenheid={meter.eenheid} />
        </td>
      ))}

      <td style={{ ...cel, textAlign: "right" }}>
        <button
          type="button"
          aria-expanded={open}
          onClick={onOpen}
          title={open ? "De rijen erachter dichtklappen" : "Toon de rijen erachter"}
          style={{
            background: "none",
            border: `1px solid ${color.border}`,
            borderRadius: radius.input,
            color: color.soft,
            cursor: "pointer",
            padding: "4px 9px",
            fontSize: size.meta,
            fontFamily: "inherit",
            lineHeight: 1.2,
          }}
        >
          <span aria-hidden>{open ? "−" : "+"}</span>
          <span style={voorlezen}>{`${meter.naam}: de rijen erachter`}</span>
        </button>
      </td>
    </tr>
  );
}

/** Wat er in één cel staat: de waarde groot, daaronder de breuk waarop hij
 *  rust, de vorige stand met het verschil, en het stoplicht van de server.
 *  Geen waarde = de reden, nooit een nul. */
function Waarde({
  waarde,
  eenheid,
}: {
  waarde: MeterkastSectorwaarde | null;
  eenheid: MeterkastMeter["eenheid"];
}) {
  if (!waarde) {
    return <span style={{ color: color.faint }}>niet gemeten in deze sector</span>;
  }
  const nu = waardeTekst(waarde.huidig, eenheid);
  const breuk = breukTekst(waarde.huidig);
  const vorige = waardeTekst(waarde.basis, eenheid);
  const delta = verschilTekst(waarde.verschil, eenheid);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {nu !== null ? (
          <span
            style={{
              ...cijfer,
              fontSize: 19,
              fontWeight: weight.bold,
              color: color.ink,
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
            }}
          >
            {nu}
          </span>
        ) : (
          <span style={{ color: color.faint }}>geen waarde</span>
        )}
        <Stoplicht stand={waarde.stand} reden={waarde.reden} />
      </div>

      {breuk && (
        <div style={{ ...cijfer, color: color.faint, marginTop: 3 }}>{breuk}</div>
      )}

      <div style={{ color: color.soft, marginTop: 5, lineHeight: 1.5 }}>
        {vorige !== null ? (
          <>
            <span style={cijfer}>{`vorige: ${vorige}`}</span>
            {delta && (
              <>
                {" · "}
                <span
                  style={{ ...cijfer, color: color.body, fontWeight: weight.medium }}
                  title={verschilUitleg(waarde.verschil, eenheid) ?? undefined}
                >
                  {delta}
                </span>
              </>
            )}
          </>
        ) : (
          <span style={{ color: color.faint }}>geen vorige stand</span>
        )}
      </div>
    </div>
  );
}

/** Alleen voor een schermlezer. */
const voorlezen = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;
